/**
 * Константы проекта
 * Централизованное хранение всех захардкоженных значений
 * 
 * Для цветов используйте: import { BRAND_COLORS, UI_COLORS } from '@/constants/colors'
 */

// ===== PRICE CALCULATIONS =====
export const PRICE_MARKUP_MULTIPLIER = 1.25 // Множитель для старой цены (25% наценка)
export const PRICE_SAVINGS_RATIO = 0.25 // Доля экономии (25%)
export const MAX_PRODUCT_QUANTITY = 999 // Максимальное количество товара в корзине

// ===== PAGINATION & SCROLL =====
export const INITIAL_PRODUCT_ROWS = 4 // Сколько полных рядов грузим при открытии категории
export const LOAD_MORE_PRODUCT_ROWS = 2 // Сколько полных рядов догружаем при скролле
/** @deprecated Используйте getInitialProductsCount() — размер зависит от числа колонок сетки */
export const INITIAL_PRODUCTS_COUNT = 21
/** @deprecated Используйте getLoadMoreProductsCount() — размер зависит от числа колонок сетки */
export const LOAD_MORE_COUNT = 12
export const SIMILAR_PRODUCTS_POOL_SIZE = 32 // Сколько товаров берём из категории для подбора
export const SIMILAR_PRODUCTS_DISPLAY_COUNT = 6 // Сколько похожих показываем на карточке товара
export const INFINITE_SCROLL_ROOT_MARGIN = '900px 0px 320px 0px' // Ранняя подгрузка до низа
export const INFINITE_SCROLL_THRESHOLD = 900 // Fallback для scroll-слушателя (px)
export const HEADER_SCROLL_THRESHOLD = 10 // Порог для изменения стиля хедера (px)
export const SKELETON_ITEMS_COUNT = 8 // Количество placeholder карточек при загрузке

// ===== STORAGE KEYS =====
export const STORAGE_KEYS = {
  TOKEN: 'token',
  REFRESH: 'refresh',
  IS_LOGIN: 'isLogin'
}

// ===== AUTH =====
export const TOKEN_PREFIX = 'Bearer '
export const DEFAULT_REGION_ID = 'UmVnaW9uOjE=' // Base64 encoded region ID
export const ANONYMOUS_VIEWER = 'AnonymousViewer' // Тип анонимного пользователя

// ===== TIMERS =====
export const PASSWORD_RESET_TIMER_DURATION = 60 // Длительность таймера для кода восстановления (секунды)
export const SCROLL_THROTTLE_INTERVAL = 200 // Интервал для throttle scroll (мс)

// ===== UI CONSTANTS =====
export const GRID_COLUMNS = {
  MOBILE: 2,
  TABLET: 3,
  DESKTOP: 3,
  XL: 4
}

export const PRODUCT_TITLE_LINE_CLAMP = 2 // Максимальное количество строк для названия товара
export const PRODUCT_TITLE_MIN_HEIGHT = 2.5 // Минимальная высота названия товара (rem)
export const FORM_MAX_WIDTH = 558 // Максимальная ширина формы (px)

// ===== PRODUCT STATUS =====
export const PRODUCT_STATUS = {
  IN_STOCK: 'IN_STOCK',
  OUT_OF_STOCK: 'OUT_OF_STOCK',
  IN_STOCK_SOON: 'IN_STOCK_SOON'
}

// ===== SORT ORDERS =====
export const SORT_ORDERS = {
  CHEAPEST_FIRST: 'CHEAPEST_FIRST',
  EXPENSIVE_FIRST: 'EXPENSIVE_FIRST',
  ALPHABETICALLY: 'ALPHABETICALLY',
  NEWEST_FIRST: 'NEWEST_FIRST'
}

// Значение сортировки по умолчанию
export const DEFAULT_SORT_ORDER = SORT_ORDERS.CHEAPEST_FIRST

// ===== COMPANY INFO =====
export const COMPANY_NAME = 'ООО "КРОН"'
export const DEFAULT_PAGE_TITLE = 'КРОН — интернет-магазин инструментов и оборудования'

// ===== ALL CATEGORIES (virtual category page) =====
export const ALL_CATEGORIES_SLUG = 'vse-kategorii'
export const ALL_CATEGORIES_PATH = `/category/${ALL_CATEGORIES_SLUG}`
export const ALL_CATEGORIES_TITLE = 'Все категории'
export const ALL_CATEGORIES_DESCRIPTION = 'Весь ассортимент интернет-магазина в одном каталоге'

// ===== ROUTES =====
export const ROUTES = {
  CATALOG: ALL_CATEGORIES_PATH,
  LOGIN: '/login/',
  REGISTRATION: '/registration/',
  CART: '/cart/',
  CHECKOUT: '/checkout/',
  PASSWORD_RESET: '/password-reset/',
  POLICY: '/policy'
}

// ===== FALLBACK IMAGES =====
export const FALLBACK_IMAGES = {
  NO_IMAGE: '/no-image.webp'
}

// ===== SEO =====
export const SEO_DESCRIPTION_LENGTH = 160 // Максимальная длина описания для SEO (символов)

// ===== ERROR MESSAGES =====
export const ERROR_MESSAGES = {
  CHECKOUT: {
    INVALID_INPUTS: 'Проверьте корректность телефона и адреса перед оформлением.',
    OUT_OF_STOCK: 'В корзине остались товары, которых нет в наличии. Удалите их, чтобы продолжить.',
    CREATE_ORDER_FAILED: 'Не удалось оформить заказ. Попробуйте ещё раз.',
    ADD_ADDRESS_FAILED: 'Не удалось добавить адрес доставки',
    GET_ADDRESS_ID_FAILED: 'Не удалось получить ID адреса доставки',
    SERVER_RESPONSE_FAILED: 'Не удалось получить ответ от сервера',
    ORDER_NUMBER_FAILED: 'Не удалось получить номер заказа',
    UNKNOWN_ERROR: 'Неизвестная ошибка при оформлении заказа'
  },
  CART: {
    DELETE_ITEM: (productName) => `Точно хотите удалить "${productName}" из корзины?`,
    CLEAR_CART: 'Вы уверены, что хотите очистить всю корзину?'
  },
  AUTH: {
    LOGOUT: 'Точно хотите выйти?'
  },
  ADDRESS: {
    DELETE: 'Вы уверены, что хотите удалить этот адрес?'
  }
}

// ===== SEARCH =====
export const MIN_SEARCH_QUERY_LENGTH = 3 // Минимальная длина поискового запроса
export const MIN_CATEGORY_SEARCH_LENGTH = 2 // Поиск внутри категории — короче, т.к. область уже ограничена
export const CATEGORY_SEARCH_DEBOUNCE_MS = 250
export const HEADER_SEARCH_RESULTS_COUNT = 15 // Количество результатов поиска в хедере

// ===== QUANTITY =====
export const MAX_QUANTITY_ALLOWED = 500000 // Максимально допустимое количество товара
